import React from 'react';
import { Lightbulb } from 'lucide-react';
import { Separator } from '../ui/separator';

/**
 * WhatsNextAdvice - Advice section shown after generating a patient letter
 * Provides guidance on next steps, what to say, and realistic expectations
 */
const WhatsNextAdvice = () => {
  return (
    <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
      <h4 className="font-bold text-sm text-primary flex items-center gap-2">
        <Lightbulb className="w-4 h-4" />
        What's Next?
      </h4>
      
      {/* Edit/Regenerate tip */}
      <div className="text-xs text-muted-foreground">
        <p className="font-semibold text-foreground mb-1">Need to add more details?</p>
        <p>Add any missing information to the notes section above and click "Generate Letter Draft" again. We'll create an updated version for you.</p>
      </div>
      
      <Separator className="bg-border/50" />
      
      {/* Next Steps */}
      <div className="text-xs space-y-2">
        <p className="font-semibold text-foreground">Your Next Steps:</p>
        <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
          <li><span className="text-foreground font-medium">Download</span> the letter (Word or PDF)</li>
          <li><span className="text-foreground font-medium">Request an appointment</span> for "Administrative/Paperwork" or "Forms Review"</li>
          <li><span className="text-foreground font-medium">Upload or bring</span> this draft to your appointment</li>
        </ol>
      </div>
      
      <Separator className="bg-border/50" />
      
      {/* Script for appointment line */}
      <div className="text-xs">
        <p className="font-semibold text-foreground mb-1">What to Say When Calling:</p>
        <div className="bg-secondary/50 p-2 rounded text-muted-foreground italic">
          "Hi, I need to schedule an administrative appointment for paperwork. I have forms that need to be reviewed and signed by my provider. Can I send them in advance through the portal so they can be reviewed before my appointment?"
        </div>
      </div>
      
      <Separator className="bg-border/50" />
      
      {/* Realistic Expectations Disclaimer */}
      <div className="text-xs">
        <p className="font-semibold text-foreground mb-1">Setting Realistic Expectations:</p>
        <div className="text-muted-foreground space-y-2">
          <p>
            <span className="text-foreground font-medium">This is a template, not a medical decision.</span> It's designed to support either approval or denial based on your provider's clinical judgment. Every provider is different—some appreciate having documentation prepared in advance, while others prefer to complete it themselves.
          </p>
          <p>
            We know paperwork can be challenging for everyone involved. This tool is meant to be <span className="text-foreground">helpful and time-efficient</span>, not to replace medical decision-making.
          </p>
          <p className="font-medium text-foreground mt-2">This may require two appointments:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li><span className="text-foreground">Re-establish care</span> — Update imaging, reactivate referrals, restart physical therapy, review your treatment plan</li>
            <li><span className="text-foreground">Paperwork review</span> — Provider reviews everything and completes/signs your forms</li>
          </ol>
          <p className="mt-2">
            Your provider may look at this and identify what needs to be updated first. <span className="text-foreground font-medium">Be ready to actively participate</span>—check if your referrals are current, if you need new imaging, and follow through on your care plan.
          </p>
        </div>
      </div>
      
      <Separator className="bg-border/50" />
      
      {/* Empowering message for re-establishing care */}
      <div className="text-xs">
        <p className="font-semibold text-foreground mb-1">Haven't Been Seen in a While?</p>
        <p className="text-muted-foreground mb-2">
          It's okay if you stopped treatment or lost track of your care plan. Many people do, especially after hearing difficult options or when life gets busy. This is your chance to:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-1">
          <li>Re-establish your plan of care</li>
          <li>Discuss where you left off and explore new options</li>
          <li>Get the documentation you need for work/duty</li>
          <li>Show you're committed to actively managing your health</li>
        </ul>
        <p className="text-primary mt-2 font-medium">You're taking a positive step by being here. Your provider is there to help, not judge.</p>
      </div>
      
      <Separator className="bg-border/50" />
      
      {/* Closing Thank You Message */}
      <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
        <p className="text-sm text-foreground font-medium mb-2">
          You Have What You Need
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          You're prepared for a productive conversation with your provider. This is <span className="text-foreground">your care</span>, and you're taking an active role in it. 
          Thank you for using WriteRight to help facilitate your communication—we're honored to be part of your journey.
        </p>
        <p className="text-xs text-primary mt-3 font-medium">
          Wishing you a successful appointment!
        </p>
      </div>
    </div>
  );
};

export default WhatsNextAdvice;
